import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { fetchImageBySection } from '../services/api';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [headerLogo, setHeaderLogo] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const loadData = async () => {
      try {
        const logo = await fetchImageBySection('header-logo');
        if (logo) setHeaderLogo(logo.url);
      } catch (error) {
        console.error('Error loading navbar data:', error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { 
      label: 'About Me', 
      hasDropdown: true,
      dropdownItems: [
        { path: '/about', label: 'About' },
        { path: '/etiquette', label: 'Etiquette' },
        { path: '/travel', label: 'Travel' },
        { path: '/gallery', label: 'Gallery' }
      ]
    },
    { path: '/services', label: 'Services' },
    { path: '/tribute-gifts', label: 'Tribute & Gifts' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* Background blur layer */}
      <div 
        className="absolute inset-0 pb-8"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 30%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.1) 80%, transparent 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          maskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
          pointerEvents: 'none'
        }}
      />
      
      {/* Content layer - separate from masked background */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-5">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src={headerLogo} 
              alt="Lady Taveenah" 
              className="h-14 md:h-16 lg:h-20 w-auto"
              style={{ display: headerLogo ? 'block' : 'none' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link, idx) => (
              link.hasDropdown ? (
                <div
                  key={idx}
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(idx)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button className="text-white/90 hover:text-white flex items-center gap-1 text-sm tracking-wide uppercase font-light">
                    {link.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </button>
                  {/* Dropdown - positioned outside masked area */}
                  <div 
                    className={`absolute top-full left-0 mt-2 w-48 border border-white/10 overflow-hidden transition-all duration-200 shadow-xl ${
                      activeDropdown === idx ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.95)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)'
                    }}
                  >
                    {link.dropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setActiveDropdown(null)}
                        className={`block px-5 py-3 text-sm tracking-wide transition-colors ${
                          location.pathname === item.path
                            ? 'bg-red-800 text-white'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm tracking-wide uppercase font-light transition-colors ${
                    location.pathname === link.path
                      ? 'text-red-500'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link to="/booking">
              <Button className="bg-red-800 hover:bg-red-700 text-white px-6 py-2 text-sm tracking-wide uppercase font-light rounded-none">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.95)', 
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)'
          }}
        >
          <div className="px-4 py-6 space-y-1">
            {navLinks.map((link, idx) => (
              link.hasDropdown ? (
                <div key={idx} className="py-2">
                  <div className="text-white/60 text-xs uppercase tracking-wider mb-2 px-2">
                    {link.label}
                  </div>
                  <div className="space-y-1">
                    {link.dropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block py-2 px-4 text-sm ${
                          location.pathname === item.path
                            ? 'text-red-500 bg-white/5'
                            : 'text-white/80'
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-2 text-sm uppercase tracking-wide ${
                    location.pathname === link.path
                      ? 'text-red-500'
                      : 'text-white/90'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <div className="pt-4">
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-red-800 hover:bg-red-700 text-white py-3 text-sm tracking-wide uppercase rounded-none">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
